import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { AppShell } from "../../components/AppShell";
import { PageHeader } from "../../components/PageHeader";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import { useCreateMaster, useMasterOptions } from "../../lib/api";
import {
  backTarget, blocking, recordValues, returnTo, sectionsFor, validate, vehicleValues,
  type Kind, type Problem, type Values,
} from "../../lib/master";
import { fmtValue } from "../../lib/format";
import { modulesFor } from "../../lib/nav";
import { Field, Section, type Option } from "./MasterFields";

/**
 * Create a master record: an item, an asset (with its vehicle), a customer, a supplier (D-142).
 *
 * **Master data is created here and nowhere else.** The add-ons read it: OneFleet mirrors assets, parts and parties
 * from the ERP and refuses to push a code the ERP never gave it, so a truck or a spare part that does not exist
 * cannot be invented from a fleet screen — its *New …* button opens this one, with `?from=` so the person is
 * handed back to where they were (the owner's rule, 2026-09-23). Opened without it, the person goes back to a module their
 * role opens — a customer to Order management first (OS-0671), not to the fleet screens a sales representative cannot open.
 *
 * One screen for four records: the fields, their order and which ones the ERP demands live in `lib/master.ts`, and
 * the endpoint re-runs the same rules before it writes. The vehicle half appears only when the asset is a vehicle,
 * because ERPNext keeps the plate, the odometer and the fuel type on a record of its own.
 */

const OPTION_LIST = (raw: unknown): Option[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) =>
    typeof row === "string"
      ? { value: row, label: row }
      : {
          value: String((row as Record<string, unknown>).name ?? ""),
          label: String(
            (row as Record<string, unknown>).item_name ??
              (row as Record<string, unknown>).employee_name ??
              (row as Record<string, unknown>).sales_person_name ??
              (row as Record<string, unknown>).name ??
              "",
          ),
        },
  );
};

export default function MasterForm({ kind }: { kind: Kind }) {
  const { t, i18n } = useTranslation();
  const { identity } = useAuth();
  const [params] = useSearchParams();
  const from = params.get("from");
  const [values, setValues] = useState<Values>(() => ({
    is_vehicle: kind === "asset" && params.get("kind") === "vehicle",
    is_stock_item: kind === "item" ? true : undefined,
    license_plate: params.get("plate") ?? undefined,
    // the fleet mirrors one item group; its parts screen says which, so a part lands where it will be seen
    item_group: params.get("group") ?? undefined,
    [kind === "item" ? "item_name" : kind === "asset" ? "asset_name" : `${kind}_name`]: params.get("name") ?? undefined,
  }));
  const [touched, setTouched] = useState(false);
  const options = useMasterOptions(kind);
  const create = useCreateMaster(kind);
  const problems: Problem[] = useMemo(() => validate(kind, values), [kind, values]);
  const made = create.data;

  const set = (field: string) => (v: unknown) => setValues((current) => ({ ...current, [field]: v }));

  const submit = () => {
    setTouched(true);
    if (blocking(problems).length > 0) return;
    create.mutate({ values: recordValues(kind, values), vehicle: kind === "asset" ? vehicleValues(values) : null });
  };

  const opens = new Set(modulesFor(identity?.roles ?? []).map((m) => m.key));
  const backTo = backTarget(kind, from, (m) => opens.has(m));
  // the credit limit is Finance's (engines/master.credit_limit_outcome): for anybody else the form says it is a proposal
  const maySetCredit = Boolean((options.data as Record<string, unknown> | undefined)?.may_set_credit);
  const labelFor = (field: string) => (field === "credit_limit" && !maySetCredit ? t("master.field.credit_limit_proposed") : t(`master.field.${field}`));
  const hintFor = (field: string) =>
    field === "credit_limit" ? t(maySetCredit ? "master.fieldHint.credit_limit" : "master.fieldHint.credit_limit_proposed")
      : t(`master.fieldHint.${kind}.${field}`, t(`master.fieldHint.${field}`, ""));

  return (
    <AppShell subtitle={t("master.title")} record={t(`master.kind.${kind}`)}>
      <PageHeader
        title={t(`master.new.${kind}`)}
        subtitle={t("master.hint")}
        actions={
          <div className="flex flex-wrap items-center gap-2" data-master-actions>
            <Link
              to={backTo}
              data-master-cancel
              className="rounded border border-brand-grey-light px-3 py-1.5 text-xs font-semibold hover:border-brand-primary"
            >
              {t("common.cancel")}
            </Link>
            <button
              type="button"
              onClick={submit}
              disabled={create.isPending || Boolean(made)}
              data-master-save
              className="rounded bg-brand-primary px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              {create.isPending ? t("master.saving") : t("master.create")}
            </button>
          </div>
        }
      />

      {create.isError && (
        <div className="mt-4">
          <ErrorBox error={create.error} />
        </div>
      )}
      {touched && blocking(problems).length > 0 && (
        <Card className="mt-4 border-l-4 border-status-red bg-red-50/50 px-4 py-2 text-sm" data-master-blocked>
          {t("master.blocked", { n: blocking(problems).length })}
        </Card>
      )}
      {made && (
        <Card className="mt-4 border-l-4 border-status-green bg-green-50/50 px-4 py-3 text-sm" data-master-made>
          <p className="font-semibold">{t("master.made", { name: made.label || made.name })}</p>
          <p className="mt-0.5 text-[11px] text-brand-grey">
            {made.vehicle ? t("master.madeVehicle", { name: made.vehicle }) : t("master.arrives")}
          </p>
          {made.credit && (
            <p className="mt-0.5 text-[11px] text-brand-charcoal" data-master-credit={made.credit.decided ? "set" : "proposed"}>
              {t(made.credit.decided ? "master.creditSet" : "master.creditProposed", { amount: fmtValue(made.credit.amount, "CDF", i18n.language), company: made.credit.company })}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <Link
              to={returnTo(backTo, kind, made.name)}
              data-master-back
              className="rounded bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white"
            >
              {t("master.back")}
            </Link>
            <button
              type="button"
              onClick={() => {
                create.reset();
                setValues({ is_vehicle: values.is_vehicle, is_stock_item: values.is_stock_item });
                setTouched(false);
              }}
              data-master-another
              className="rounded border border-brand-grey-light px-3 py-1.5 text-xs font-semibold hover:border-brand-primary"
            >
              {t("master.another")}
            </button>
          </div>
        </Card>
      )}

      {options.isPending && <Skeleton className="mt-4 h-64" />}
      {options.isError && (
        <div className="mt-4">
          <ErrorBox error={options.error} onRetry={() => void options.refetch()} />
        </div>
      )}

      {options.data && !made && (
        <div className="mt-4 space-y-4">
          {kind === "asset" && (
            <Card className="flex items-center gap-2 px-4 py-3 text-sm" data-master-vehicle-toggle>
              <input
                type="checkbox"
                checked={Boolean(values.is_vehicle)}
                onChange={(e) => set("is_vehicle")(e.target.checked)}
                data-master-input="is_vehicle"
                className="h-4 w-4 rounded border-brand-grey-light"
              />
              <span>
                <span className="font-medium">{t("master.isVehicle")}</span>
                <span className="mt-0.5 block text-[11px] text-brand-grey">{t("master.isVehicleHint")}</span>
              </span>
            </Card>
          )}
          {sectionsFor(kind, values).map((section) => (
            <Section
              key={section.section}
              hook={section.section}
              title={t(`master.section.${kind}.${section.section}`, t(`master.section.${section.section}`))}
            >
              {section.fields.map((spec) => (
                <Field
                  key={spec.field}
                  spec={spec}
                  label={labelFor(spec.field)}
                  hint={hintFor(spec.field)}
                  value={values[spec.field] ?? ""}
                  problems={touched ? problems : problems.filter((p) => p.severity === "warning")}
                  options={
                    spec.choices
                      ? spec.choices.map((c) => ({ value: c, label: t(`master.choice.${c}`, c) }))
                      : OPTION_LIST((options.data as Record<string, unknown>)[spec.options ?? ""])
                  }
                  onChange={set(spec.field)}
                />
              ))}
            </Section>
          ))}
        </div>
      )}
    </AppShell>
  );
}
